import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  AanslagBtw,
  AanslagVpb,
  AanslagZwv,
  BerichtenService,
  BerichtSbt,
  Bezwaarschrift,
  BokkzAlgDataService,
  BokkzClient, Box1InkomenUitWerkEnWoning,
  BtwAangifte,
  ClientEntity,
  DossierItemEntity,
  IntermediaryEntity,
  Jaarrapportage,
  OphaalResponse,
  PdfRequest,
  PdfRequestOptie,
  PdfRequestSoort,
  PdfRequestType,
  PmClient,
  RenderRequest,
  Toeslagen,
  Voorheffingen,
  VpbAangifte,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {firstValueFrom, Observable, shareReplay, tap} from 'rxjs';
import * as JSZip from 'jszip';
import {ToastService} from '@snelstart/ui-toast';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {dispatchSavedItemEvent} from '@snelstart/domain-logic';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";

type PreviewBestand = {
  blob: Blob;
  filename: string;
};

@Component({
  selector: 'snelstart-venture-fiscaal-feature-workflow-item',
  templateUrl: './feature-workflow-item.component.html',
  styleUrls: ['./feature-workflow-item.component.scss'],
  imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiButtonModule, SharedUiAlertModule, CommonModule],
})
export class FeatureWorkflowItemComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private readonly destroyRef = inject(DestroyRef);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly pmClient = inject(PmClient);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly berichtenService = inject(BerichtenService);
  private readonly toastService = inject(ToastService);
  private readonly sanitizer= inject(DomSanitizer);

  public pdfPreviewUrl?: SafeResourceUrl;
  private pdfPreviewObjectUrl?: string;
  public isZipTooLarge = false;
  private officename: any;
  private clientId: any;
  private taxId: any;
  private achternaam: any;

  readonly intermediaryEntity$: Observable<IntermediaryEntity> =
    this.algDataService.intermediaryEntity$.pipe(
      tap(data => {
        this.officename = data.officeName;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly clientEntity$: Observable<ClientEntity> =
    this.algDataService.clientEntity$.pipe(
      tap(data => {
        this.clientId = data.id;
        this.achternaam = data.achternaam;
        this.taxId = data.taxId;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly workflowItem$: Observable<DossierItemEntity> =
    this.algDataService.workflowItem$.pipe(
      tap(data => {
        this.dossierItemFg.reset();
        this.dossierItemFg.patchValue(data);
        this.toonPdfPreview().then(() => {
          console.log('PDF preview set successfully');
        });
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public dossierItemFg = new FormGroup({
    id: new FormControl<number | null>(null),
    clientId: new FormControl<number | null>(null),
    title: new FormControl<string | null>(null),
    creator: new FormControl<string | null>(null),
    dateCreation: new FormControl<Date | null>(null),
    dateDeadline: new FormControl<Date | null>(null),
    hasDeadline: new FormControl<boolean | null>(false),
    isChecked: new FormControl<boolean | null>(false),
    opmerkingen: new FormControl<string | null>(null),
    attachment: new FormControl<any | null>(null),
    nameAttachment: new FormControl<string | null>(null),
    checked: new FormControl<boolean | null>(false),
    type: new FormControl<string | null>(null),
    year: new FormControl<string | null>(null),
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];


  ngOnInit(): void {
    this.intermediaryEntity$.subscribe();
    this.clientEntity$.subscribe();
  }

  ngOnDestroy(): void {
    this.saveForm();
    if (this.pdfPreviewObjectUrl) {
      URL.revokeObjectURL(this.pdfPreviewObjectUrl);
    }
  }

  saveForm(): void {
    this.dossierItemFg.updateValueAndValidity({ emitEvent: false });

    if (this.dossierItemFg.invalid) {
      this.dossierItemFg.markAllAsTouched();
      return;
    }

    if (!this.clientId) {
      this.toastService.showWarningMessage('ClientId ontbreekt.');
      return;
    }

    const formValue = this.dossierItemFg.getRawValue();

    const dossierItemEntity: DossierItemEntity = {
      ...formValue,
      id: formValue.id ?? undefined,
      clientId: formValue.clientId ?? undefined,
      title: formValue.title ?? undefined,
      creator: formValue.creator ?? undefined,
      dateCreation: formValue.dateCreation ?? undefined,
      dateDeadline: formValue.dateDeadline ?? undefined,
      hasDeadline: formValue.hasDeadline ?? undefined,
      isChecked: formValue.isChecked ?? undefined,
      opmerkingen: formValue.opmerkingen ?? undefined,
      attachment: formValue.attachment ?? undefined,
      nameAttachment: formValue.nameAttachment ?? undefined,
      checked: formValue.checked ?? undefined,
      type: formValue.type ?? undefined,
      year: formValue.year ?? undefined,
    };

    this.bokkzClient
      .postDossierItem(String(this.clientId), dossierItemEntity)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedEntity) => {
          this.algDataService.updateWorkflowItem(savedEntity);

          dispatchSavedItemEvent({
            identifier: savedEntity.id!,
            result: savedEntity,
          });

          this.dossierItemFg.patchValue(savedEntity, {
            emitEvent: false,
          });
          this.toastService.showSuccessMessage('Het dossier item is succesvol opgeslagen.');
        },
        error: () => {
          this.toastService.showWarningMessage('Het dossier item kon niet worden opgeslagen.');
        },
      });
  }

  async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target) {
          resolve(event.target.result as ArrayBuffer);
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };
      reader.onerror = (event) => {
        reject(new Error('Failed to read file: ' + event.target?.error));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  onFileSelected(event: Event) {
    const formData = this.dossierItemFg;
    if (event.target) {
      const target = event.target as HTMLInputElement;
      if (target.files) {
        const file: File = target.files[0];
        const zip = new JSZip();
        (async () => {
          // Read the file as an ArrayBuffer
          const arrayBuffer = await this.readFileAsArrayBuffer(file);
          zip.file(file.name, arrayBuffer);

          try {
            // Generate the ZIP archive as a base64-encoded string
            const zipContent = await zip.generateAsync({type: 'base64'});

            const zipSizeInBytes = (zipContent.length * 3) / 4; // Base64 encoding increases size by ~33%
            const sizeInMB = zipSizeInBytes / (1024 * 1024);

            console.log('ZIP size (MB):', sizeInMB.toFixed(2));

            if (sizeInMB > 5) {
              console.error('The ZIP file exceeds 5 MB!');
              this.isZipTooLarge = true; // Example: Use this in your template to disable a button
            } else {
              this.isZipTooLarge = false;
              formData.patchValue({attachment: JSON.stringify(zipContent)});
            }
          } catch (error) {
            console.error('Error generating ZIP archive:', error);
          }
        })();
      }
    }
  }

  async toonPdf(): Promise<void> {
    const bestanden = await this.maakBijlageBestanden();

    for (const bestand of bestanden) {
      this.downloadBlob(bestand.blob, bestand.filename);
    }
  }

  async toonPdfPreview(): Promise<void> {
    const bestanden = await this.maakBijlageBestanden();

    const pdfBestand = bestanden.find(bestand =>
      bestand.filename.toLowerCase().endsWith('.pdf') ||
      bestand.blob.type === 'application/pdf'
    );

    if (!pdfBestand) {
      this.pdfPreviewUrl = undefined;
      return;
    }

    if (this.pdfPreviewObjectUrl) {
      URL.revokeObjectURL(this.pdfPreviewObjectUrl);
    }

    const pdfBlob = new Blob([pdfBestand.blob], { type: 'application/pdf' });
    this.pdfPreviewObjectUrl = URL.createObjectURL(pdfBlob);

    this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.pdfPreviewObjectUrl
    );
  }

  private async maakBijlageBestanden(): Promise<PreviewBestand[]> {
    const attachment = this.dossierItemFg.value.attachment;
    const creator = this.dossierItemFg.value.creator;
    const title = this.dossierItemFg.value.title as string;
    const tijd = this.dossierItemFg.getRawValue().dateCreation;

    const blob = this.dataURItoBlob(attachment);
    const zipFile = await JSZip.loadAsync(blob);
    const bestanden: PreviewBestand[] = [];

    for (const relativePath of Object.keys(zipFile.files)) {
      const zipEntry = zipFile.files[relativePath];

      if (zipEntry.dir) {
        continue;
      }

      const fileBlob = await zipEntry.async('blob');
      const fileArray = await fileBlob.text();

      if (fileArray.includes('xbrl') && !fileArray.includes('bezwaarSpecificatie')) {
        const ophaal = this.berichtSoortBepaler(fileArray);
        const data = await firstValueFrom(this.berichtenService.converteerXbrlToJson(ophaal));
        const pdfRequest = this.pdfSbaBepaler(data, ophaal);
        const pdfStringBase64 = await firstValueFrom(this.bokkzClient.getPdfRapport(pdfRequest));

        const filenameWithoutExtension = relativePath.replace(/\.[^/.]+$/, '');
        const timestamp = new Date().toISOString().replace(/:/g, '_').replace(/\./g, '_');

        bestanden.push({
          blob: this.dataURItoBlob(pdfStringBase64),
          filename: `${filenameWithoutExtension}_DownloadDate_${timestamp}.pdf`,
        });
      } else if (this.isJsonObject(fileArray) && title && title.toLowerCase().includes('jaarrapportage')) {
        const jaarRapportage = JSON.parse(fileArray) as Jaarrapportage;

        const render = {
          requestedReport: 'KleinPubs',
          licensee: '12345678',
          preparedBy: this.officename,
          copies: 3,
          outputFormat: 'PDF',
          outputLanguage: 'nl',
          stylesheet: 'PMReport',
          debugOptions: {
            showElementStyles: false,
            showSbrId: false,
            showNotInKvK: true,
            showJrElement: false,
            showMappingNotFound: false,
          },
          financialStatement: jaarRapportage,
        } as RenderRequest;

        const response = await firstValueFrom(this.pmClient.postPmRenderRequestPublicatiestukken(render));

        bestanden.push({
          blob: this.dataURItoBlob(response.base64File),
          filename:
            'Publicatiestukken ' +
            jaarRapportage.informatieRapportage?.financialReportingPeriodCurrentEndDate?.value +
            '-' +
            this.achternaam +
            '.pdf',
        });
      } else if (fileArray.includes('Loonaangifte')) {
        const pdfRequest = {} as PdfRequest;
        pdfRequest.loonAangifte = fileArray;
        pdfRequest.concept = false;
        pdfRequest.soort = PdfRequestSoort.Aangifte;
        pdfRequest.optie = PdfRequestOptie.Loonaangifte;
        pdfRequest.type = PdfRequestType.LB;
        pdfRequest.time = tijd?.toISOString();

        const pdfStringBase64 = await firstValueFrom(this.bokkzClient.getPdfRapport(pdfRequest));

        bestanden.push({
          blob: this.dataURItoBlob(pdfStringBase64),
          filename: relativePath,
        });
      } else if (this.isJsonObject(fileArray) && title) {
        const pdfRequest = this.pdfAangifteBepaler(title, fileArray, tijd!);
        const pdfStringBase64 = await firstValueFrom(this.bokkzClient.getPdfRapport(pdfRequest));

        let filename = relativePath;

        if (title.toLowerCase().includes('aangifte inkomstenbelasting')) {
          filename = 'Aangifte_IB_' + this.achternaam + '_' + this.extractSubstring(relativePath);
        } else if (title.toLowerCase().includes('opgave icp')) {
          filename = relativePath.replace(/^BTW_Q_/, '');
        }

        bestanden.push({
          blob: this.dataURItoBlob(pdfStringBase64),
          filename,
        });
      } else {
        let filename = relativePath;

        if (creator && creator.includes('Migratie')) {
          const isZip = await this.isZipFile(fileBlob);

          if (isZip) {
            filename = filename.replace(/\.[^/.]+$/, '') + '.zip';
          }
        }

        bestanden.push({
          blob: fileBlob,
          filename,
        });
      }
    }

    return bestanden;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  async isZipFile(blob: Blob) {
    const header = await blob.slice(0, 4).arrayBuffer();
    const bytes = new Uint8Array(header);
    return bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
  }

  extractSubstring(input: string): string | null {
    const match = input.match(/ingediend.*/);
    return match ? match[0] : null;
  }

  dataURItoBlob(dataURI: string) {
    if (dataURI != null) {
      const byteString = atob(dataURI.replace(/"/g, ''));
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      return new Blob([int8Array], {type: 'application/zip'});
    } else throw 'No dataobject';
  }

  berichtSoortBepaler(xbrl: string): OphaalResponse {
    let ophaalResponse = {} as OphaalResponse;
    if (xbrl.includes('bd-rpt-ih-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'IB',
      };
    } else if (xbrl.includes('bd-rpt-zvw-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'ZVW',
      };
    } else if (xbrl.includes('bd-rpt-vpb-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'VPB',
      };
    } else if (xbrl.includes('bd-rpt-tsl-sbt')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'TSG',
      };
    } else if (xbrl.includes('bd-rpt-ihz-via')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'VIA',
      };
    } else if (xbrl.includes('bd-rpt-ob-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'OB',
      };
    } else if (xbrl.includes('uitstel-sba')) {
      ophaalResponse = {
        inhoud: btoa(xbrl),
        berichtsoort: 'BECON',
      };
    }
    return ophaalResponse;
  }

  pdfSbaBepaler(data: Aangifte | Toeslagen | VpbAangifte | BtwAangifte, ophaal: OphaalResponse): PdfRequest {
    const pdfRequest = {} as PdfRequest;
    if (ophaal.berichtsoort.includes('IB')) {
      pdfRequest.aanslagIbJaar1 = data;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.IB;
    } else if (ophaal.berichtsoort.includes('ZVW')) {
      pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.ZVW;
    } else if (ophaal.berichtsoort.includes('VPB')) {
      pdfRequest.aanslagVpbJaar1 = data as AanslagVpb;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.VPB;
    } else if (ophaal.berichtsoort.includes('TSG')) {
      pdfRequest.toeslagenJaar1 = data as BerichtSbt;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Beschikking;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.TSL;
    } else if (ophaal.berichtsoort.includes('OB')) {
      pdfRequest.aanslagBtwJaar1 = data as AanslagBtw;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aanslag;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.BTW;
    } else if (ophaal.berichtsoort.includes('VIA')) {
      pdfRequest.aangifteJaar1 = data as Aangifte;
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Via;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.IB;
    } else if (ophaal.berichtsoort.includes('BECON')) {
      pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
      pdfRequest.concept = false;
    }
    return pdfRequest;
  }

  pdfAangifteBepaler(title: string, jsondata: string, tijd?: Date): PdfRequest {
    const pdfRequest = {} as PdfRequest;
    if (title.toLowerCase().includes('bevestiging indienen aangifte inkomstenbelasting')) {
      pdfRequest.aangifteJaar1 = this.bepaalAangever(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.IB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('bevestiging indienen aangifte vennootschapsbelasting')) {
      pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.VPB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('bezwaarprodecure')) {
      const bezwaar = JSON.parse(jsondata) as Bezwaarschrift;
      if (bezwaar.bezwaarSpecificatie?.bijlagen) {
        bezwaar.bezwaarSpecificatie.bijlagen = bezwaar.bezwaarSpecificatie.bijlagen.map((bijlage) => ({
          filename: bijlage.filename,
          content: '',
        }));
        pdfRequest.bezwaarschrift = bezwaar;
      } else {
        pdfRequest.bezwaarschrift = bezwaar;
      }
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Bezwaar;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.BZW;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('bevestiging indienen opgave icp')) {
      pdfRequest.opgaafIcp = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Opgaaf;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.ICP;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('aangifte vpb')) {
      pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.VPB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('aangifte ib')) {
      pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.IB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('bevestiging indienen verzoek of wijziging voorlopige aanslag inkomstenbelasting')) {
      pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Verzoek;
      pdfRequest.optie = PdfRequestOptie.Berekeningen;
      pdfRequest.type = PdfRequestType.IB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('bevestiging indienen verzoek of wijziging voorlopige aanslag vennootschapsbelasting')) {
      pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Verzoek;
      pdfRequest.optie = PdfRequestOptie.Berekeningen;
      pdfRequest.type = PdfRequestType.VPB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('bevestiging indienen aangifte dividendbelasting')) {
      pdfRequest.aangifteDivBel = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.DB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('bevestiging indienen aangifte schenkbelasting')) {
      pdfRequest.aangifteSchenkBel = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.SB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('vpb') && title.toLowerCase().includes('va')) {
      pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Verzoek;
      pdfRequest.optie = PdfRequestOptie.Berekeningen;
      pdfRequest.type = PdfRequestType.VPB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('ib') && title.toLowerCase().includes('va')) {
      pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Verzoek;
      pdfRequest.optie = PdfRequestOptie.Berekeningen;
      pdfRequest.type = PdfRequestType.IB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('btw')) {
      pdfRequest.aangifteBtwJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.BTW;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('tsl')) {
      pdfRequest.berekeningsJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Verzoek;
      pdfRequest.optie = PdfRequestOptie.Berekeningen;
      pdfRequest.type = PdfRequestType.TSL;
    } else if (title.toLowerCase().includes('toeslagen')) {
      pdfRequest.berekeningsJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Verzoek;
      pdfRequest.optie = PdfRequestOptie.Berekeningen;
      pdfRequest.type = PdfRequestType.TSL;
    } else if (title.toLowerCase().includes('verwerkte via')) {
      pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Via;
      pdfRequest.optie = PdfRequestOptie.GeheleRapport;
      pdfRequest.type = PdfRequestType.IB;
      pdfRequest.time = tijd?.toISOString();
    } else if (title.toLowerCase().includes('loonheffingen')) {
      pdfRequest.loonAangifte = JSON.parse(jsondata);
      console.log("test " + pdfRequest.loonAangifte);
      pdfRequest.concept = false;
      pdfRequest.soort = PdfRequestSoort.Aangifte;
      pdfRequest.optie = PdfRequestOptie.Loonaangifte;
      pdfRequest.type = PdfRequestType.LB;
      pdfRequest.time = tijd?.toISOString();
    } else {
      console.log(title);
      console.log(jsondata);
      console.log(tijd);
      this.toastService.showErrorMessage('Unsupported pdf');
    }
    return pdfRequest;
  }

  private isJsonObject(content: string): boolean {
    try {
      const parsed = JSON.parse(content);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch (error) {
      return false;
    }
  }

  private bepaalAangever(jsonData: string) {
    const aangifte = JSON.parse(jsonData) as Aangifte;
    if (aangifte.aangever.bsn === this.taxId) {
      return aangifte;
    } else {
      return this.convertAangifteToPartnerAangifte(aangifte);
    }
  }

  private convertAangifteToPartnerAangifte(aangifte: Aangifte) {
    if (aangifte.partnerAangifte) {
      const partnerAangifte = structuredClone(aangifte.partnerAangifte);
      partnerAangifte.partnerAangifte = structuredClone(aangifte);
      partnerAangifte.partnerAangifte.partnerAangifte = undefined;

      if (aangifte.kinderen != null) {
        partnerAangifte.kinderen = aangifte.kinderen;
      }

      if (aangifte.box1 != null && aangifte.box1.belastbareInkomstenUitEigenWoning != null) {
        if (partnerAangifte.box1 == null) {
          partnerAangifte.box1 = <Box1InkomenUitWerkEnWoning>{};
        }
        partnerAangifte.box1.belastbareInkomstenUitEigenWoning = aangifte.box1.belastbareInkomstenUitEigenWoning;
      }

      if (aangifte.box2 != null) {
        partnerAangifte.box2 = aangifte.box2;
      }

      if (aangifte.box3 != null) {
        partnerAangifte.box3 = aangifte.box3;
      }

      if (aangifte.persoonsgebondenAftrek != null) {
        partnerAangifte.persoonsgebondenAftrek = aangifte.persoonsgebondenAftrek;
      }

      const aangeverVoorheffingen = aangifte.voorheffingen;

      if (aangeverVoorheffingen != null && aangeverVoorheffingen.ingehoudenDividendbelasting != null) {
        if (partnerAangifte.voorheffingen == null) {
          partnerAangifte.voorheffingen = {} as Voorheffingen;
        }
        partnerAangifte.voorheffingen.ingehoudenDividendbelasting = aangeverVoorheffingen.ingehoudenDividendbelasting;
      }

      if (aangeverVoorheffingen != null && aangeverVoorheffingen.buitenlandseBronheffing != null) {
        if (partnerAangifte.voorheffingen == null) {
          partnerAangifte.voorheffingen = {} as Voorheffingen;
        }
        partnerAangifte.voorheffingen.buitenlandseBronheffing = aangeverVoorheffingen.buitenlandseBronheffing;
      }

      if (aangifte.verdeling != null) {
        partnerAangifte.verdeling = aangifte.partnerAangifte.verdeling;
        partnerAangifte.partnerAangifte.verdeling = aangifte.verdeling;
      }

      partnerAangifte.gezamenlijkeAangifte = aangifte.gezamenlijkeAangifte;
      partnerAangifte.aangever.burgerlijkeStaat = aangifte.aangever.burgerlijkeStaat;
      return partnerAangifte;
    }

    return aangifte;
  }
}
